
import React, { useState, useEffect, useCallback } from 'react';
import { AnalysisState, HistoryItem, Language } from './types';
import { identifyRock } from './services/geminiService';
import { saveToHistory, getHistory, deleteHistoryItem, clearHistory } from './services/storageService';
import { translations } from './translations';
import { ImageUploader } from './components/ImageUploader';
import { AnalysisResult } from './components/AnalysisResult';
import { DiagnosticPanel } from './components/DiagnosticPanel';
import { Spinner } from './components/Spinner';
import { HistoryDrawer } from './components/HistoryDrawer';
import { Mountain, AlertCircle, Award, Layers, History, ShieldAlert, NotebookText, Languages, Crown, CheckCircle2, QrCode, X, Settings, Plus, Upload, RefreshCw } from 'lucide-react';

interface AdminCode {
  id: string;
  credits: number;
  used: boolean;
  createdAt: string;
}

interface FeedbackItem {
  id: string;
  correctName: string;
  originalName: string;
  reason: string;
  image?: string;
  status: string;
  timestamp: number;
}

interface DistilledRule {
  id: string;
  rule: string;
  timestamp: number;
  originalContext?: {
    originalName: string;
    correctName: string;
  };
}

const App: React.FC = () => {
  const [lang, setLang] = useState<Language>('zh');
  const t = translations[lang];

  const [userId, setUserId] = useState<string>(() => {
    const saved = localStorage.getItem('stone_master_user_id');
    if (saved) return saved;
    const newId = 'user_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('stone_master_user_id', newId);
    return newId;
  });

  const handleSyncId = (newId: string) => {
    if (!newId || !newId.startsWith('user_')) {
      alert('无效的恢复 ID');
      return;
    }
    setUserId(newId);
    localStorage.setItem('stone_master_user_id', newId);
    alert('设备同步成功！正在加载云端数据...');
  };

  const [usage, setUsage] = useState<{ count: number; lastReset: string; credits: number; isPro: boolean }>(() => {
    const saved = localStorage.getItem('stone_master_usage');
    if (saved) {
      const parsed = JSON.parse(saved);
      const today = new Date().toDateString();
      if (parsed.lastReset !== today) {
        return { ...parsed, count: 0, lastReset: today };
      }
      return parsed;
    }
    return { count: 0, lastReset: new Date().toDateString(), credits: 0, isPro: false };
  });

  // Persist usage to localStorage
  useEffect(() => {
    localStorage.setItem('stone_master_usage', JSON.stringify(usage));
  }, [usage]);

  // Sync usage with server on mount
  useEffect(() => {
    fetch(`/api/usage?userId=${userId}`)
      .then(res => res.json())
      .then(data => {
        if (data && !data.error) {
          setUsage(data);
        }
      })
      .catch(err => console.error('Failed to sync usage', err));
  }, [userId]);

  const [showPayModal, setShowPayModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminKeyInput, setAdminKeyInput] = useState('');
  const [adminData, setAdminData] = useState<{ 
    codes: AdminCode[]; 
    stats: { total: number; used: number };
    feedback?: FeedbackItem[];
    rules?: DistilledRule[];
  } | null>(null);
  const [adminTab, setAdminTab] = useState<'codes' | 'feedback' | 'rules'>('codes');
  const [adminSearch, setAdminSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [hasMoreCodes, setHasMoreCodes] = useState(true);
  const [titleClicks, setTitleClicks] = useState(0);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemError, setRedeemError] = useState('');
  const [redeemSuccess, setRedeemSuccess] = useState(false);
  const [redeemSuccessMsg, setRedeemSuccessMsg] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('/wechat_qr.png');
  const [qrStatus, setQrStatus] = useState<'loading' | 'success' | 'error'>('loading');

  useEffect(() => {
    // Pre-fetch QR code on mount
    fetch('/api/public/qr')
      .then(res => res.json())
      .then(data => {
        if (data.qrCode) {
          setQrCodeUrl(data.qrCode);
          setQrStatus('success');
        } else {
          setQrStatus('error');
        }
      })
      .catch(err => {
        console.error('Failed to fetch QR code', err);
        setQrStatus('error');
      });
  }, []);

  useEffect(() => {
    localStorage.setItem('stone_master_usage', JSON.stringify(usage));
  }, [usage]);

  const [state, setState] = useState<AnalysisState>({
    isLoading: false,
    data: null,
    error: null,
    images: [],
    diagnosticFeedback: {}
  });

  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  
  useEffect(() => {
    console.log("App v8.8.8 Mounted");
    document.title = "石头大人 - 专业岩石鉴定";
  }, []);

  useEffect(() => {
    const items = getHistory();
    setHistoryItems(items);
  }, []);

  const handleImagesSelect = async (images: { base64: string; mimeType: string; label?: string }[]) => {
    // Check limits - Strict mode (no free tries)
    if (!usage.isPro && usage.credits <= 0) {
      setShowPayModal(true);
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null, images, data: null, diagnosticFeedback: {} }));
    try {
      const analysis = await identifyRock(images, lang, undefined, userId);
      setState(prev => ({ ...prev, isLoading: false, data: analysis }));
      
      // Update usage from server
      fetch(`/api/usage?userId=${userId}`)
        .then(res => res.json())
        .then(data => {
          if (data && !data.error) setUsage(data);
        });

      // Use the first image as thumbnail for history
      if (images.length > 0) {
        saveToHistory(analysis, images[0].base64).then(setHistoryItems);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t.errorGeneric;
      setState(prev => ({ ...prev, isLoading: false, error: errorMsg }));
    }
  };

  const handleDiagnosticSubmit = async (feedback: Record<string, string>) => {
    // Note: If you want diagnostic submissions to also cost credits, leave this check.
    // Otherwise, you can remove it if you consider recalibration "free" for the same image.
    if (!usage.isPro && usage.credits <= 0) {
      setShowPayModal(true);
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null, diagnosticFeedback: feedback }));
    try {
      const analysis = await identifyRock(state.images, lang, feedback, userId);
      setState(prev => ({ ...prev, isLoading: false, data: analysis }));
      
      // Update usage from server
      fetch(`/api/usage?userId=${userId}`)
        .then(res => res.json())
        .then(data => {
          if (data && !data.error) setUsage(data);
        });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t.errorGeneric;
      setState(prev => ({ ...prev, isLoading: false, error: errorMsg }));
    }
  };

  const handleReset = () => {
    setState({ isLoading: false, data: null, error: null, images: [], diagnosticFeedback: {} });
  };

  const toggleLang = () => {
    setLang(prev => prev === 'zh' ? 'en' : 'zh');
  };

  const handleRedeem = async () => {
    setRedeemError('');
    try {
      const response = await fetch('/api/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: redeemCode, userId })
      });
      
      const result = await response.json();
      console.log('Redeem result:', result);
      
      if (result.success) {
        // 🛡️ FIX: Use the absolute credits returned by server, don't add locally
        const newCredits = result.credits;
        setUsage(prev => ({ ...prev, credits: newCredits }));
        setRedeemSuccessMsg(`${result.message || t.redeemSuccess} (当前总额度: ${newCredits})`);
        setRedeemSuccess(true);
        setTimeout(() => {
          setRedeemSuccess(false);
          setRedeemSuccessMsg('');
          setShowPayModal(false);
          setRedeemCode('');
        }, 2500);
      } else {
        setRedeemError(t.invalidCode);
      }
    } catch (err) {
      console.error(err);
      setRedeemError(t.errorGeneric);
    }
  };

  const fetchAdminData = useCallback(async (searchStr?: string, lastId?: string) => {
    const trimmedKey = adminKeyInput.trim();
    if (!trimmedKey) return; // Don't fetch if key is empty

    try {
      const encodedKey = encodeURIComponent(trimmedKey);
      
      let statusUrl = `/api/admin/status?key=${encodedKey}`;
      if (searchStr) {
        statusUrl += `&search=${encodeURIComponent(searchStr)}`;
      } else if (lastId) {
        statusUrl += `&lastId=${encodeURIComponent(lastId)}`;
      }

      const [statusRes, feedbackRes, rulesRes] = await Promise.all([
        fetch(statusUrl),
        fetch(`/api/admin/feedback?key=${encodedKey}`),
        fetch(`/api/admin/rules?key=${encodedKey}`)
      ]);

      if (statusRes.ok) {
        const statusData = await statusRes.json();
        
        if (!feedbackRes.ok) console.error('Failed to fetch feedback:', await feedbackRes.text());
        if (!rulesRes.ok) console.error('Failed to fetch rules:', await rulesRes.text());

        const feedbackData = feedbackRes.ok ? await feedbackRes.json() : [];
        const rulesData = rulesRes.ok ? await rulesRes.json() : [];
        
        setAdminData(prev => {
          if (lastId && prev) {
            return {
              ...statusData,
              codes: [...prev.codes, ...statusData.codes],
              feedback: feedbackData,
              rules: rulesData
            };
          } else {
            return {
              ...statusData,
              feedback: feedbackData,
              rules: rulesData
            };
          }
        });
        
        setHasMoreCodes(statusData.codes.length === 50);
      } else {
        const errText = await statusRes.text();
        alert('管理员密钥错误: ' + errText);
      }
    } catch (err) {
      console.error(err);
      alert('连接服务器失败');
    } finally {
      setIsSearching(false);
    }
  }, [adminKeyInput]);

  useEffect(() => {
    if (adminSearch.length >= 2) {
      const timer = setTimeout(() => {
        setIsSearching(true);
        fetchAdminData(adminSearch);
      }, 500);
      return () => clearTimeout(timer);
    } else if (adminSearch.length === 0) {
      fetchAdminData();
    }
  }, [adminSearch, fetchAdminData]);

  const distillFeedback = async (feedbackId: string) => {
    try {
      const response = await fetch('/api/admin/distill', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: adminKeyInput, feedbackId })
      });
      if (response.ok) {
        const result = await response.json();
        alert('蒸馏成功！新准则：' + result.rule);
        fetchAdminData();
      } else {
        alert('蒸馏失败');
      }
    } catch (err) {
      console.error(err);
      alert('操作出错');
    }
  };

  const deleteFeedback = async (feedbackId: string) => {
    try {
      const response = await fetch('/api/admin/delete-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: adminKeyInput, feedbackId })
      });
      if (response.ok) {
        fetchAdminData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const deleteRule = async (ruleId: string) => {
    try {
      const response = await fetch('/api/admin/delete-rule', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: adminKeyInput, ruleId })
      });
      if (response.ok) {
        fetchAdminData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const generateCode = async () => {
    try {
      const response = await fetch('/api/admin/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: adminKeyInput, credits: 100, count: 1 })
      });
      if (response.ok) {
        fetchAdminData();
      }
    } catch (err) {
      console.error(err);
      alert('生成失败');
    }
  };

  const clearUsedCodes = async () => {
    try {
      const response = await fetch('/api/admin/clear-used', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: adminKeyInput })
      });
      if (response.ok) {
        const result = await response.json();
        alert(`成功清理 ${result.count || 0} 个已使用的卡密`);
        fetchAdminData();
      } else {
        alert('清理失败');
      }
    } catch (err) {
      console.error(err);
      alert('清理出错');
    }
  };

  const handleQRUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = async () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      const MAX_WIDTH = 800;
      const MAX_HEIGHT = 800;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > MAX_WIDTH) { height *= MAX_WIDTH / width; width = MAX_WIDTH; }
      } else {
        if (height > MAX_HEIGHT) { width *= MAX_HEIGHT / height; height = MAX_HEIGHT; }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      // Fill white background in case of transparent PNG
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      const base64String = canvas.toDataURL('image/jpeg', 0.7);

      try {
        const response = await fetch('/api/admin/upload-qr', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key: adminKeyInput, image: base64String })
        });
        if (response.ok) {
          alert('收款码上传成功！刷新页面即可看到。');
        } else {
          try {
            const data = await response.json();
            alert('上传失败: ' + (data.error || '未知错误'));
          } catch {
            alert('上传失败，文件可能过大');
          }
        }
      } catch (err) {
        console.error(err);
        alert('上传出错');
      }
    };
    img.src = url;
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fafaf9] text-stone-900 font-sans selection:bg-emerald-200 relative">
      <HistoryDrawer isOpen={isHistoryOpen} onClose={() => setIsHistoryOpen(false)} items={historyItems} onSelect={(item) => { setState({ isLoading: false, data: item.data, error: null, images: [{ base64: item.thumbnail, mimeType: 'image/jpeg' }], diagnosticFeedback: {} }); setIsHistoryOpen(false); }} onDelete={(id, e) => { e.stopPropagation(); setHistoryItems(deleteHistoryItem(id)); }} onClearAll={() => { clearHistory(); setHistoryItems([]); setIsHistoryOpen(false); }} lang={lang} />

      {/* Admin Modal */}
      {showAdminModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[110] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white w-full max-w-md rounded-3xl overflow-hidden shadow-2xl animate-scale-in">
            <div className="bg-stone-900 p-6 text-white flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Settings className="text-amber-500" size={20} />
                <h2 className="text-lg font-serif-sc font-bold">后台管理系统</h2>
              </div>
              <div className="flex items-center gap-3">
                {adminData && (
                  <button 
                    onClick={fetchAdminData}
                    className="text-stone-400 hover:text-white transition-all hover:rotate-180 duration-500"
                    title="刷新数据"
                  >
                    <RefreshCw size={20} />
                  </button>
                )}
                <button onClick={() => { setShowAdminModal(false); setAdminData(null); setAdminTab('codes'); }} className="text-stone-400 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6 max-h-[85vh] overflow-y-auto">
              {!adminData ? (
                <div className="space-y-4">
                  <p className="text-sm text-stone-600">请输入管理员密钥以继续：</p>
                  <input 
                    type="password" 
                    value={adminKeyInput}
                    onChange={(e) => setAdminKeyInput(e.target.value)}
                    className="w-full px-4 py-3 bg-stone-100 border border-stone-200 rounded-lg outline-none"
                    placeholder="ADMIN-XXXXXX"
                  />
                  <button 
                    onClick={fetchAdminData}
                    className="w-full bg-stone-800 text-white py-3 rounded-lg font-bold"
                  >
                    登录后台
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Tabs */}
                  <div className="flex bg-stone-100 p-1 rounded-xl">
                    <button 
                      onClick={() => setAdminTab('codes')}
                      className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${adminTab === 'codes' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'}`}
                    >
                      卡密管理
                    </button>
                    <button 
                      onClick={() => setAdminTab('feedback')}
                      className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${adminTab === 'feedback' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'}`}
                    >
                      专家反馈 ({adminData.feedback?.length || 0})
                    </button>
                    <button 
                      onClick={() => setAdminTab('rules')}
                      className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${adminTab === 'rules' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'}`}
                    >
                      鉴定准则 ({adminData.rules?.length || 0})
                    </button>
                  </div>

                  {adminTab === 'codes' && (
                    <div className="space-y-6 animate-fade-in">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-stone-50 p-4 rounded-2xl border border-stone-100">
                          <p className="text-[10px] text-stone-400 uppercase font-bold">总卡密数</p>
                          <p className="text-2xl font-serif-sc font-bold text-stone-800">{adminData.stats.total}</p>
                        </div>
                        <div className="bg-stone-50 p-4 rounded-2xl border border-stone-100">
                          <p className="text-[10px] text-stone-400 uppercase font-bold">已使用</p>
                          <p className="text-2xl font-serif-sc font-bold text-amber-600">{adminData.stats.used}</p>
                        </div>
                      </div>

                      <div className="bg-red-50 p-4 rounded-2xl border border-red-100">
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-[10px] text-red-400 uppercase font-bold">设备数据清理</p>
                        </div>
                        <p className="text-[10px] text-red-500 mb-3">清理数据库中无效的设备号（无余额且超过30天未活跃的免费用户），减少无痕浏览产生的垃圾数据。</p>
                        <button
                          onClick={async () => {
                            if (!confirm('确定要清理无效的设备数据吗？')) return;
                            try {
                              const res = await fetch('/api/admin/cleanup-users', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ key: adminKeyInput })
                              });
                              const data = await res.json();
                              if (data.success) {
                                alert(`清理成功！共删除了 ${data.deletedCount} 条无效设备数据。`);
                              } else {
                                alert('清理失败: ' + data.error);
                              }
                            } catch {
                              alert('请求失败');
                            }
                          }}
                          className="w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2"
                        >
                          <RefreshCw size={14} /> 清理无效设备
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <button 
                          onClick={generateCode}
                          className="bg-amber-500 hover:bg-amber-600 text-stone-900 py-4 rounded-2xl font-bold shadow-lg transition-all flex items-center justify-center gap-2"
                        >
                          <Plus size={20} /> 生成新卡密
                        </button>
                        <button 
                          onClick={clearUsedCodes}
                          className="bg-stone-100 hover:bg-stone-200 text-stone-600 py-4 rounded-2xl font-bold border border-stone-200 transition-all flex items-center justify-center gap-2"
                          title="清理额度已耗尽的卡密"
                        >
                          <X size={20} /> 清理已耗尽
                        </button>
                      </div>

                      <div className="p-4 bg-stone-50 rounded-2xl border border-stone-100 space-y-3">
                        <p className="text-[10px] text-stone-400 uppercase font-bold">更新收款二维码</p>
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-stone-300 rounded-xl cursor-pointer hover:bg-stone-100 transition-all">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="text-stone-400 mb-2" size={24} />
                            <p className="text-xs text-stone-500">点击上传支付宝收款二维码</p>
                            <p className="text-[10px] text-stone-400 mt-1">支持 PNG, JPG</p>
                          </div>
                          <input type="file" className="hidden" accept="image/*" onChange={handleQRUpload} />
                        </label>
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between items-end">
                          <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest">卡密列表</h3>
                          <span className="text-[9px] text-stone-400 font-medium">
                            {adminSearch ? `搜索结果` : `显示最近 ${adminData.codes.length} 个`}
                          </span>
                        </div>

                        <div className="relative">
                          <input 
                            type="text"
                            placeholder="搜索卡密 (输入前几位)..."
                            value={adminSearch}
                            onChange={(e) => setAdminSearch(e.target.value)}
                            className="w-full px-4 py-2 bg-stone-100 border border-stone-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-amber-500/20"
                          />
                          {isSearching && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <RefreshCw size={14} className="animate-spin text-stone-400" />
                            </div>
                          )}
                        </div>

                        <div className="space-y-2">
                          {adminData.codes.map((code: AdminCode) => (
                            <div key={code.id} className={`p-3 rounded-xl border flex justify-between items-center ${code.used ? 'bg-stone-50 border-stone-100 opacity-60' : 'bg-white border-stone-200 shadow-sm'}`}>
                              <div>
                                <p className="font-mono font-bold text-stone-800">{code.id}</p>
                                <p className="text-[10px] text-stone-400">{new Date(code.createdAt).toLocaleString()}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {code.used ? (
                                  <span className="text-[10px] bg-stone-200 text-stone-500 px-2 py-1 rounded-full">已激活</span>
                                ) : (
                                  <button 
                                    onClick={() => { navigator.clipboard.writeText(code.id); alert('已复制到剪贴板'); }}
                                    className="text-[10px] bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-bold hover:bg-amber-200"
                                  >
                                    复制
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        {hasMoreCodes && !adminSearch && (
                          <button 
                            onClick={() => {
                              const lastId = adminData?.codes[adminData.codes.length - 1].id;
                              fetchAdminData(undefined, lastId);
                            }}
                            className="w-full py-3 text-stone-500 text-xs font-bold hover:bg-stone-50 rounded-xl transition-all border border-dashed border-stone-200"
                          >
                            加载更多卡密...
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {adminTab === 'feedback' && (
                    <div className="space-y-4 animate-fade-in">
                      {adminData.feedback?.length === 0 ? (
                        <p className="text-center py-10 text-stone-400 text-sm">暂无专家反馈</p>
                      ) : (
                        adminData.feedback?.map((f: FeedbackItem) => (
                          <div key={f.id} className="p-4 bg-white border border-stone-200 rounded-2xl shadow-sm space-y-3">
                            <div className="flex justify-between items-start">
                              <div className="flex items-center gap-2">
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${f.status === 'distilled' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {f.status === 'distilled' ? '已蒸馏' : '待处理'}
                                </span>
                                <span className="text-[10px] text-stone-400">{new Date(f.timestamp).toLocaleString()}</span>
                              </div>
                              <button onClick={() => deleteFeedback(f.id)} className="p-2 text-stone-400 hover:text-red-500 transition-colors" title="删除反馈">
                                <X size={20} />
                              </button>
                            </div>
                            <div className="flex gap-3">
                              {f.image && <img src={f.image} className="w-16 h-16 object-cover rounded-lg border border-stone-100" />}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold text-stone-800 truncate">{f.originalName} → {f.correctName}</p>
                                <p className="text-[11px] text-stone-500 line-clamp-2 mt-1 italic">"{f.reason}"</p>
                              </div>
                            </div>
                            {f.status !== 'distilled' && (
                              <button 
                                onClick={() => distillFeedback(f.id)}
                                className="w-full py-2 bg-stone-800 text-white text-xs font-bold rounded-lg hover:bg-stone-900 transition-all flex items-center justify-center gap-2"
                              >
                                <Crown size={14} className="text-amber-400" /> 采纳并蒸馏为准则
                              </button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {adminTab === 'rules' && (
                    <div className="space-y-4 animate-fade-in">
                      {adminData.rules?.length === 0 ? (
                        <p className="text-center py-10 text-stone-400 text-sm">暂无鉴定准则</p>
                      ) : (
                        adminData.rules?.map((r: DistilledRule) => (
                          <div key={r.id} className="p-4 bg-emerald-50/50 border border-emerald-100 rounded-2xl space-y-2 relative group">
                            <button 
                              onClick={() => deleteRule(r.id)}
                              className="absolute top-2 right-2 p-2 text-emerald-300 hover:text-red-500 transition-all"
                              title="删除准则"
                            >
                              <X size={20} />
                            </button>
                            <div className="flex items-center gap-2 mb-1">
                              <Award size={14} className="text-emerald-600" />
                              <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">专家准则</span>
                            </div>
                            <p className="text-xs text-stone-800 leading-relaxed font-medium">{r.rule}</p>
                            <div className="pt-2 border-t border-emerald-100 flex justify-between items-center">
                              <span className="text-[9px] text-emerald-600/60 italic">
                                源自: {r.originalContext?.originalName} → {r.originalContext?.correctName}
                              </span>
                              <span className="text-[9px] text-stone-400">{new Date(r.timestamp).toLocaleDateString()}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pay/Upgrade Modal */}
      {showPayModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 md:p-4 bg-stone-900/90 backdrop-blur-md animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] overflow-hidden animate-scale-in flex flex-col">
            <div className="bg-stone-800 p-4 md:p-6 text-white flex justify-between items-center shrink-0">
              <div className="flex items-center gap-3">
                <Crown className="text-amber-400" />
                <h3 className="font-serif-sc font-bold text-lg">{t.upgradePro}</h3>
              </div>
              <button onClick={() => setShowPayModal(false)} className="hover:rotate-90 transition-transform p-1"><X size={24} /></button>
            </div>
            
            <div className="p-6 md:p-8 space-y-6 md:space-y-8 overflow-y-auto">
              <div className="text-center space-y-2">
                <div className={`p-4 rounded-xl border transition-all ${usage.credits > 0 ? 'bg-amber-50 border-amber-100 animate-pulse-slow' : 'bg-stone-50 border-stone-100'}`}>
                  <p className={`${usage.credits > 0 ? 'text-amber-800' : 'text-stone-600'} font-bold text-lg`}>
                    {t.creditsRemaining}: {usage.credits}
                  </p>
                  {usage.credits > 0 ? (
                    <p className="text-amber-600 text-xs mt-1">专业版额度已激活，您可以继续鉴定</p>
                  ) : (
                    <>
                      <p className="text-stone-600 font-serif-sc mt-2">{t.limitReached}</p>
                      <p className="text-sm text-stone-400">{t.limitReachedSub}</p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-center gap-4 p-4 md:p-6 bg-stone-50 rounded-xl border border-stone-100">
                <div className="w-56 h-72 md:w-64 md:h-80 bg-white p-2 rounded-lg shadow-md flex items-center justify-center border border-stone-200 overflow-hidden relative group">
                  {qrStatus === 'loading' ? (
                    <div className="flex flex-col items-center gap-2">
                      <RefreshCw className="animate-spin text-stone-300" size={32} />
                      <p className="text-[10px] text-stone-400">正在加载收款码...</p>
                    </div>
                  ) : qrStatus === 'success' ? (
                    <img 
                      src={qrCodeUrl} 
                      alt="Alipay QR" 
                      className="w-full h-full object-contain animate-fade-in"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-4 text-stone-400 p-4 text-center animate-fade-in">
                      <QrCode size={48} className="text-stone-300" />
                      <div className="space-y-2">
                        <p className="text-[10px] font-bold text-amber-600">⚠️ 收款码图片未就位</p>
                        <p className="text-[9px] leading-relaxed">请管理员在后台上传收款码</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <p className="font-serif-sc font-bold text-stone-800 text-base md:text-lg">{t.buyPro}</p>
                  <p className="text-xs text-emerald-600 font-bold mt-1 max-w-sm mx-auto">{t.orderRemark}</p>
                </div>
              </div>

              <div className="space-y-4 pb-4">
                <div className="p-3 bg-stone-50 rounded-xl border border-stone-100 flex flex-col items-center gap-1">
                  <span className="text-[9px] text-stone-400 uppercase font-bold tracking-widest">您的设备恢复 ID (Recovery ID)</span>
                  <div className="flex items-center gap-2">
                    <code className="text-[10px] font-mono text-stone-600 bg-white px-2 py-1 rounded border border-stone-200">{userId}</code>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(userId);
                        alert('恢复 ID 已复制，请妥善保存。');
                      }}
                      className="p-1 hover:bg-stone-200 rounded transition-colors text-stone-400"
                      title="复制恢复 ID"
                    >
                      <NotebookText size={14} />
                    </button>
                    <button 
                      onClick={() => {
                        const input = prompt('请输入另一台设备的恢复 ID 以同步额度：');
                        if (input) handleSyncId(input.trim());
                      }}
                      className="p-1 hover:bg-stone-200 rounded transition-colors text-emerald-600"
                      title="同步其他设备"
                    >
                      <RefreshCw size={14} />
                    </button>
                  </div>
                  <p className="text-[8px] text-stone-400 text-center leading-tight mt-1">若更换设备或清除缓存，可凭此 ID 或激活码找回额度</p>
                </div>

                <div className="relative">
                  <input 
                    type="text" 
                    placeholder={t.enterCode}
                    value={redeemCode}
                    onChange={(e) => setRedeemCode(e.target.value)}
                    className="w-full px-4 py-3 bg-stone-100 border border-stone-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all font-mono tracking-widest text-center"
                  />
                  {redeemError && <p className="text-red-500 text-[10px] mt-1 text-center animate-shake">{redeemError}</p>}
                  {redeemSuccess && (
                    <div className="absolute inset-0 bg-emerald-500 text-white flex flex-col items-center justify-center rounded-lg animate-fade-in z-10 p-2 text-center">
                      <CheckCircle2 className="mb-1" size={24} />
                      <p className="text-xs font-bold leading-tight">{redeemSuccessMsg}</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={handleRedeem}
                    disabled={!redeemCode || redeemSuccess}
                    className="flex-grow bg-stone-800 hover:bg-stone-900 disabled:bg-stone-300 text-white py-3 rounded-lg font-serif-sc font-bold transition-all shadow-lg hover:shadow-xl active:scale-[0.98]"
                  >
                    {t.redeem}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <header className="bg-stone-800 text-stone-200 sticky top-0 z-40 shadow-xl border-b border-stone-700 safe-area-top">
          <div className="max-w-6xl mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
            <div 
              className="flex items-center gap-3 cursor-pointer hover:opacity-90 group select-none" 
              onClick={() => {
                const newClicks = titleClicks + 1;
                setTitleClicks(newClicks);
                if (newClicks >= 5) {
                  setShowAdminModal(true);
                  setTitleClicks(0);
                }
              }}
            >
              <div className="w-9 h-9 md:w-10 md:h-10 bg-gradient-to-br from-stone-700 to-stone-900 rounded-lg flex items-center justify-center border border-stone-600"><Mountain className="text-emerald-500" size={20} /></div>
              <div className="flex flex-col">
                <h1 className="text-lg md:text-xl font-serif-sc font-bold tracking-widest text-stone-100">{t.appName}</h1>
                <span className="text-[9px] md:text-[10px] text-stone-400 uppercase tracking-[0.2em]">{t.appSub}</span>
              </div>
            </div>

          <div className="flex items-center gap-2">
            <div className="flex flex-col items-end mr-2">
              <span className="text-[8px] md:text-[10px] text-stone-400 uppercase tracking-tighter">{t.creditsRemaining}</span>
              <span className="text-[10px] md:text-xs font-bold text-amber-500">{usage.isPro ? t.unlimited : (usage.credits > 0 ? usage.credits : 0)}</span>
            </div>
            <button onClick={() => setShowPayModal(true)} className="p-2 rounded-full bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 transition-all mr-1" title={t.upgradePro}>
              <Crown size={18} />
            </button>
            <button onClick={() => setIsHistoryOpen(true)} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-stone-700 hover:bg-stone-600 border border-stone-600 text-stone-300 transition-all">
              <NotebookText size={16} className="text-amber-500" /><span className="text-xs font-serif-sc hidden md:inline">{t.history}</span>
            </button>
            <button onClick={toggleLang} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-stone-700 hover:bg-stone-600 border border-stone-600 text-emerald-400 transition-all font-bold text-xs">
              <Languages size={14} /><span>{lang === 'zh' ? 'EN' : '中'}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-grow px-2 md:px-4 py-4 md:py-16 relative overflow-x-hidden pb-48">
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23292524' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }}></div>
        <div className="max-w-6xl mx-auto relative z-10">
          {!state.data && !state.isLoading && (
            <div className="text-center mb-12 md:mb-20 space-y-6 animate-fade-in pt-6 md:pt-10 px-4">
              <h2 className="text-3xl md:text-6xl font-serif-sc font-bold text-stone-800 mb-4 tracking-tight leading-tight">{t.slogan}<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-stone-700 to-stone-500 relative inline-block px-2 mt-2">{t.sloganSub}<span className="absolute -bottom-1 left-0 w-full h-1 bg-emerald-700/20"></span></span></h2>
              <p className="text-base md:text-xl text-stone-500 max-w-2xl mx-auto font-serif-sc leading-relaxed">{t.heroDesc}</p>
            </div>
          )}
          <div className="transition-all duration-500 ease-out min-h-[400px]">
            {state.isLoading ? <div className="flex flex-col items-center py-20"><Spinner lang={lang} /></div> : state.data && state.images.length > 0 ? (
              <div className="space-y-8">
                <AnalysisResult 
                  data={state.data} 
                  image={state.images[0].base64} 
                  onReset={handleReset} 
                  lang={lang} 
                />
                {state.data.diagnosticTests && state.data.diagnosticTests.length > 0 && (
                  <DiagnosticPanel 
                    tests={state.data.diagnosticTests} 
                    onSubmit={handleDiagnosticSubmit} 
                    lang={lang}
                    initialFeedback={state.diagnosticFeedback}
                  />
                )}
              </div>
            ) : (
              <div className="space-y-16 md:space-y-24 px-4">
                <div className="max-w-3xl mx-auto transform hover:-translate-y-1 transition-transform duration-300">
                   <ImageUploader onImagesSelect={handleImagesSelect} lang={lang} />
                   {state.error && <div className="mt-6 bg-red-50 border-l-4 border-red-500 text-red-700 px-6 py-4 rounded-r shadow-sm flex items-start gap-3 animate-shake"><AlertCircle size={24} className="shrink-0 mt-0.5" /><div><h3 className="font-bold text-sm uppercase tracking-wider mb-1">{t.errorTitle}</h3><p className="text-sm">{state.error}</p></div></div>}
                </div>
                {!state.error && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 border-t border-stone-200 pt-12 md:pt-16 max-w-6xl mx-auto">
                    <FeatureCard icon={<Layers className="text-stone-700" size={24} />} title={t.feature1Title} desc={t.feature1Desc} />
                    <FeatureCard icon={<History className="text-stone-700" size={24} />} title={t.feature2Title} desc={t.feature2Desc} />
                    <FeatureCard icon={<Award className="text-stone-700" size={24} />} title={t.feature3Title} desc={t.feature3Desc} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="bg-stone-800 text-stone-400 py-8 md:py-12 border-t border-stone-700 safe-area-bottom">
        <div className="max-w-6xl mx-auto px-6">
           <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
             <div className="flex items-center gap-3 opacity-80"><div className="w-8 h-8 bg-stone-900 rounded flex items-center justify-center border border-stone-700"><Mountain size={16} className="text-stone-300" /></div><span className="font-serif-sc font-bold text-stone-200 tracking-widest">{t.appName}</span></div>
             <div className="flex flex-col md:flex-row gap-4 md:gap-8 text-xs text-stone-500"><span>© {new Date().getFullYear()} Stone Master AI Lab.</span></div>
           </div>
           <div className="bg-stone-900/30 p-4 rounded border border-stone-700 flex gap-3 items-start"><ShieldAlert size={20} className="text-amber-700/70 shrink-0 mt-0.5" /><div className="text-[10px] md:text-xs leading-relaxed text-stone-500 text-justify"><p className="font-bold text-stone-400 mb-1">{t.disclaimerTitle}</p>{t.disclaimer}</div></div>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard: React.FC<{ icon: React.ReactNode, title: string; desc: string }> = ({ icon, title, desc }) => (
  <div className="p-6 rounded-sm hover:bg-white hover:shadow-xl transition-all duration-500 group border border-transparent hover:border-stone-200 bg-[#fcfcfc] shadow-sm">
    <div className="flex items-center gap-3 mb-4"><div className="p-2 bg-stone-100 rounded-full group-hover:bg-amber-100">{icon}</div><h3 className="font-serif-sc font-bold text-lg text-stone-800 group-hover:text-amber-800">{title}</h3></div>
    <p className="text-stone-600 text-sm leading-relaxed font-serif-sc text-justify">{desc}</p>
  </div>
);

export default App;